import React, { useState } from "react";
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InputDialog from "./InputDialog";
import { ReactComponent as SidebarCollapseClose } from "../../../assets/images/icons/sidebar_collapse_icon.svg";
import { ReactComponent as SidebarCollapseOpen } from "../../../assets/images/icons/sidebar_open_icon.svg";
import { ReactComponent as HistoryIcon } from "../../../assets/images/icons/history_icon.svg";
import { ReactComponent as ArchiveIcon } from "../../../assets/images/icons/archive_icon.svg";
import { ReactComponent as EditIcon } from "../../../assets/images/icons/newchat_icon.svg";
import { ReactComponent as WorkflowIcon } from "../../../assets/images/icons/workflow_icon.svg";
import { ReactComponent as RenameIcon } from "../../../assets/images/icons/rename_icon.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/images/icons/delete_icon.svg";
import "../../../styles/pageStyles/chatbot/chatSideBar.css";
import ChatItemList from "./ChatItemList";

const NAV_ITEMS = [
  { label: "History", icon: <HistoryIcon sx={{ fontSize: 18 }} /> },
  {
    label: "Workflow",
    icon: <WorkflowIcon sx={{ fontSize: 18 }} />,
    disabled: true,
  },
  { label: "Archive", icon: <ArchiveIcon sx={{ fontSize: 18 }} /> },
];

const formatGroupDate = (isoString) => {
  if (!isoString) return "";
  try {
    const dateObj = new Date(isoString);
    return dateObj
      .toLocaleString("en-US", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .toUpperCase();
  } catch (e) {
    return "";
  }
};

const ChatSidebar = ({
  sidebarOpen,
  setSidebarOpen,
  handleNewChat,
  historyList = [],
  groupedChatsList = [],
  archiveList = [],
  isHistoryLoading,
  isArchivedLoading,
  activeSessionId,
  onSelectSession,
  onExecuteAction,
  onArchiveTabOpen,
  onHistoryTabOpen,
  archiveFetched,
  onCreateGroup,
}) => {
  const [activeNav, setActiveNav] = useState("History");
  const [searchQuery, setSearchQuery] = useState("");
  const currentUserId = localStorage.getItem("user_id");
  const [isCreateGroupDialogOpen, setIsCreateGroupDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [isRenameGroupDialogOpen, setIsRenameGroupDialogOpen] = useState(false);
  const [targetRenameGroup, setTargetRenameGroup] = useState(null);
  const [renameInputValue, setRenameInputValue] = useState("");
  const [isDeleteGroupDialogOpen, setIsDeleteGroupDialogOpen] = useState(false);
  const [targetDeleteGroup, setTargetDeleteGroup] = useState(null);
  const [isMoveToGroupDialogOpen, setIsMoveToGroupDialogOpen] = useState(false);
  const [targetChatForGrouping, setTargetChatForGrouping] = useState(null);
  const [groupSearchQuery, setGroupSearchQuery] = useState("");
  const [isRemoveFromGroupDialogOpen, setIsRemoveFromGroupDialogOpen] =
    useState(false);
  const [targetChatForRemoval, setTargetChatForRemoval] = useState(null);

  const handleRemoveFromGroupConfirm = async () => {
    if (!targetChatForRemoval) return;

    if (onExecuteAction) {
      await onExecuteAction("removeFromGroup", {
        chat: targetChatForRemoval.chat,
        groupId: targetChatForRemoval.groupId,
      });
    }

    setIsRemoveFromGroupDialogOpen(false);
    setTargetChatForRemoval(null);
  };

  const handleCreateGroupConfirm = async () => {
    if (!newGroupName.trim()) return;

    const success = await onCreateGroup(newGroupName);

    if (success) {
      setIsCreateGroupDialogOpen(false);
    }
  };

  const handleCreateGroupCancel = () => {
    setIsCreateGroupDialogOpen(false);
  };

  const handleRenameGroupConfirm = async () => {
    if (!renameInputValue.trim() || !targetRenameGroup) return;

    const updatedGroup = {
      ...targetRenameGroup,
      name: renameInputValue.trim(),
      title: renameInputValue.trim(),
    };

    if (onExecuteAction) {
      await onExecuteAction("renameFolder", updatedGroup, "History");
    }

    setIsRenameGroupDialogOpen(false);
  };

  const handleDeleteGroupConfirm = async () => {
    if (!targetDeleteGroup) return;

    if (onExecuteAction) {
      await onExecuteAction("deleteFolder", targetDeleteGroup, "History");
    }

    setIsDeleteGroupDialogOpen(false);
  };

  const handleSidebarActionTrigger = (
    actionType,
    item,
    sectionLabel,
    groupId,
  ) => {
    if (actionType === "renameFolder") {
      setTargetRenameGroup(item);
      setRenameInputValue(item.name || item.title || "Untitled Group");
      setIsRenameGroupDialogOpen(true);
    } else if (actionType === "deleteFolder") {
      setTargetDeleteGroup(item);
      setIsDeleteGroupDialogOpen(true);
    } else if (actionType === "moveToGroup") {
      if (groupedChatsList.length === 0) {
        setTargetChatForGrouping(item);
        setNewGroupName("");
        setIsCreateGroupDialogOpen(true);
      } else {
        setTargetChatForGrouping(item);
        setIsMoveToGroupDialogOpen(true);
      }
    } else if (actionType === "removeFromGroup") {
      setTargetChatForRemoval({ chat: item, groupId });
      setIsRemoveFromGroupDialogOpen(true);
    } else {
      if (onExecuteAction) {
        onExecuteAction(actionType, item, sectionLabel);
      }
    }
  };

  const handleGroupSelect = (groupId) => {
    if (onExecuteAction) {
      onExecuteAction("assignGroup", { chat: targetChatForGrouping, groupId });
    }
    setIsMoveToGroupDialogOpen(false);
    setGroupSearchQuery("");
  };

  const filteredGroupsForDialog = groupedChatsList.filter((group) =>
    (group.name || group.title || "")
      .toLowerCase()
      .includes(groupSearchQuery.toLowerCase()),
  );

  const filteredHistoryItems = historyList
    .filter((chat) => String(chat.created_by) === String(currentUserId))
    .filter((chat) =>
      (chat.title || "").toLowerCase().includes(searchQuery.toLowerCase()),
    );

  const filteredGroupedChats = groupedChatsList
    .map((group) => ({
      ...group,
      messages: (group.messages || [])
        .filter((chat) => String(chat.created_by) === String(currentUserId))
        .filter((chat) =>
          (chat.title || "").toLowerCase().includes(searchQuery.toLowerCase()),
        ),
    }))
    .filter((group) => group.messages.length > 0 || searchQuery === "");

  const filteredArchiveItems = archiveList
    .filter((chat) => String(chat.created_by) === String(currentUserId))
    .filter((chat) =>
      (chat.title || "").toLowerCase().includes(searchQuery.toLowerCase()),
    );

  return (
    <Box
      className="sidebar-container"
      style={{ "--sidebar-width": sidebarOpen ? "284px" : "88px" }}
    >
      <Box
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="sidebar-toggle"
      >
        {sidebarOpen ? <SidebarCollapseClose /> : <SidebarCollapseOpen />}
      </Box>

      {/* CLOSED SIDEBAR */}
      {!sidebarOpen && (
        <Box className="sidebar-closed-content">
          <Box
            className="new-chat-icon-btn"
            onClick={() => {
              setSidebarOpen(true);
              handleNewChat();
            }}
          >
            <EditIcon />
          </Box>
          <IconButton
            className="search-icon-btn"
            onClick={() => setSidebarOpen(true)}
          >
            <SearchIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            onClick={() => {
              setSidebarOpen(true);
              setActiveNav("History");
              if (onHistoryTabOpen) onHistoryTabOpen();
            }}
          >
            <HistoryIcon
              style={{ color: activeNav === "History" ? "#077EE0" : "#555E6D" }}
            />
          </IconButton>
          <IconButton
            disabled
            onClick={() => {
              setSidebarOpen(true);
              setActiveNav("Workflow");
            }}
            sx={{ opacity: 0.4, pointerEvents: "none" }}
          >
            <WorkflowIcon
              style={{
                color: activeNav === "Workflow" ? "#077EE0" : "#555E6D",
              }}
            />
          </IconButton>
          <IconButton
            onClick={() => {
              setSidebarOpen(true);
              setActiveNav("Archive");
              if (onArchiveTabOpen) onArchiveTabOpen();
            }}
          >
            <ArchiveIcon
              style={{ color: activeNav === "Archive" ? "#077EE0" : "#555E6D" }}
            />
          </IconButton>
        </Box>
      )}

      {/* OPEN SIDEBAR */}
      {sidebarOpen && (
        <Box className="sidebar-open-content">
          <Box className="new-chat-button" onClick={handleNewChat}>
            <EditIcon sx={{ color: "#fff", fontSize: 18 }} />
            <Box className="new-chat-text">New Chat</Box>
          </Box>
          <Box className="search-box-container">
            <SearchIcon
              sx={{ fontSize: 16, color: "#9BA8B5", flexShrink: 0 }}
            />
            <input
              placeholder="Search"
              className="search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Box>
          <Box className="nav-tabs-wrapper">
            {NAV_ITEMS.map((item) => {
              const isSelected = activeNav === item.label;
              return (
                <Box
                  key={item.label}
                  onClick={() => {
                    if (item.disabled) return;
                    setActiveNav(item.label);
                    if (item.label === "Archive" && onArchiveTabOpen) {
                      onArchiveTabOpen();
                    } else if (item.label === "History" && onHistoryTabOpen) {
                      onHistoryTabOpen();
                    }
                  }}
                  className={`nav-tab-item ${isSelected ? "selected" : ""} ${item.disabled ? "disabled" : ""}`}
                >
                  <Box className="nav-tab-icon">{item.icon}</Box>
                  <Box className="nav-tab-label">{item.label}</Box>
                </Box>
              );
            })}
          </Box>
          <Box className="section-separator" />

          {activeNav === "History" && (
            <ChatItemList
              items={filteredHistoryItems}
              groupedChats={filteredGroupedChats}
              sectionLabel="History"
              activeSessionId={activeSessionId}
              onSelectItem={onSelectSession}
              handleActionTrigger={handleSidebarActionTrigger}
              isLoading={isHistoryLoading}
              onAddGroup={() => {
                setNewGroupName("");
                setIsCreateGroupDialogOpen(true);
              }}
            />
          )}

          {activeNav === "Archive" && (
            <ChatItemList
              items={filteredArchiveItems}
              sectionLabel="Archive"
              activeSessionId={activeSessionId}
              onSelectItem={onSelectSession}
              handleActionTrigger={handleSidebarActionTrigger}
              isLoading={isArchivedLoading}
            />
          )}
        </Box>
      )}

      {/* Create Group Dialog */}
      <InputDialog
        open={isCreateGroupDialogOpen}
        title="Add New Group"
        placeholder="Enter group name..."
        confirmText="Create"
        value={newGroupName}
        onChange={setNewGroupName}
        onConfirm={handleCreateGroupConfirm}
        onCancel={handleCreateGroupCancel}
      />

      {/* Rename Group Dialog */}
      <InputDialog
        open={isRenameGroupDialogOpen}
        title="Rename Group"
        placeholder="Enter new group name..."
        confirmText="Rename"
        value={renameInputValue}
        onChange={setRenameInputValue}
        onConfirm={handleRenameGroupConfirm}
        onCancel={() => {
          setIsRenameGroupDialogOpen(false);
        }}
      />

      {/* Delete Group Confirmation Dialog */}
      <InputDialog
        open={isDeleteGroupDialogOpen}
        title="Delete Group"
        hideInput={true}
        message={
          <>
            Are you sure you want to delete the group? This action cannot be
            undone.
          </>
        }
        confirmText="Yes"
        cancelText="No"
        onConfirm={handleDeleteGroupConfirm}
        onCancel={() => {
          setIsDeleteGroupDialogOpen(false);
        }}
      />

      {/* Move to Group Dialog */}
      <InputDialog
        open={isMoveToGroupDialogOpen}
        title="Move to Group"
        hideActions={true}
        onCancel={() => {
          setIsMoveToGroupDialogOpen(false);
          setGroupSearchQuery("");
        }}
      >
        <Box sx={{ mb: 2 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search groups..."
            value={groupSearchQuery}
            onChange={(e) => setGroupSearchQuery(e.target.value)}
            className="rename-dialog-input"
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ color: "#9BA8B5", mr: 1, fontSize: 18 }} />
              ),
            }}
          />
        </Box>
        {/* Scrollable Group List */}
        <Box
          style={{
            maxHeight: "170px",
            minHeight: "120px",
            overflowY: "auto",
            paddingRight: "4px",
            marginLeft: "-8px",
            marginRight: "-8px",
          }}
        >
          <List sx={{ p: 0 }}>
            {filteredGroupsForDialog.length > 0 ? (
              filteredGroupsForDialog.map((group) => (
                <ListItemButton
                  disableRipple
                  disableTouchRipple
                  key={group.id || group.name}
                  onClick={() => handleGroupSelect(group.id)}
                  sx={{
                    py: 1,
                    px: 2,
                    mb: 0.5,
                    borderBottom: "1px solid #EFEFEF",
                    "&:last-of-type": {
                      borderBottom: "none",
                    },
                    "&:hover": {
                      backgroundColor: "rgba(7, 126, 224, 0.04)",
                    },
                  }}
                >
                  <ListItemText
                    primary={group.name || group.title || "Untitled Group"}
                    secondary={formatGroupDate(group.created_at)}
                    primaryTypographyProps={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#333",
                    }}
                    secondaryTypographyProps={{
                      fontSize: "12px",
                      color: "#9BA8B5",
                    }}
                  />
                </ListItemButton>
              ))
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  minHeight: "100px",
                }}
              >
                <Typography
                  sx={{
                    color: "#9BA8B5",
                    fontStyle: "italic",
                    fontSize: "13px",
                  }}
                >
                  {groupedChatsList.length > 0
                    ? "No matching groups found"
                    : "No groups created yet"}
                </Typography>
              </Box>
            )}
          </List>
        </Box>
      </InputDialog>

      <InputDialog
        open={isRemoveFromGroupDialogOpen}
        title="Remove from Group"
        hideInput={true}
        message="Are you sure you want to remove chat from this folder?"
        confirmText="Yes"
        cancelText="No"
        onConfirm={handleRemoveFromGroupConfirm}
        onCancel={() => {
          setIsRemoveFromGroupDialogOpen(false);
          setTargetChatForRemoval(null);
        }}
      />
    </Box>
  );
};

export default ChatSidebar;
